import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { loginUser, createOtp, verifyOtp, registerUser } from '../../store/reducers/authReducer';
import './Auth.css';
import logo from '../../assets/logo-large.png';
import MenuButton from '../Store/MenuButton';

const Auth: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const isLogin = location.pathname === '/login';
  const [showOTP, setShowOTP] = useState(false);

  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '',]);
  const [acceptPolicy, setAcceptPolicy] = useState(false);
  const [policyError, setPolicyError] = useState('');

  const { isLoading, isAuthenticated, error } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLogin) {
      try {
        if (!acceptPolicy) {
          setPolicyError('Please accept Privacy Policy & Terms.');
          return;
        } else {
          setPolicyError('');
          await dispatch(createOtp({ email, type: 'UR' })).unwrap();
          setShowOTP(true);
        }
      } catch (err) {
        console.error('OTP generation failed:', err);
      }
    } else {
      try {
        await dispatch(loginUser({ email, password })).unwrap();
      } catch (err) {
        console.error('Login failed:', err);
      }
    }
  };


  const handleOTPChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOTP = [...otp];
    newOTP[index] = value;
    setOtp(newOTP);
    if (value && index < 3) {
      const nextInput = document.querySelector(`input[name=otp-${index + 1}]`) as HTMLInputElement;
      nextInput?.focus();
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.querySelector(`input[name=otp-${index - 1}]`) as HTMLInputElement;
      prevInput?.focus();
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join('');

    try {
      const { token } = await dispatch(verifyOtp({ email, type: 'UR', otp: Number(otpValue) })).unwrap();
      const deviceToken = `${Date.now()}`;
      await dispatch(registerUser({
        email,
        password,
        otpToken: token,
        fullName,
        userName: username,
        deviceToken
      })).unwrap();

    } catch (err) {
      console.error('Verification or Sign-up failed:', err);
    }
  };


  const toggleAuth = () => {
    navigate(isLogin ? '/signup' : '/login');
  };

  return (
    <div className="auth-container">
      <MenuButton />
      <AnimatePresence mode="wait">
        {isLogin ? (
          <>
            <motion.div 
              key="login-image" 
              className="auth-image login-bg"
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 30 }}
            />
            <motion.div 
              key="login-form" 
              className="auth-form-side" 
              data-islogin={true}
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 300, opacity: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 30 }}
            >
              <div className="auth-card">
                <motion.div 
                  className="logo-container"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <img src={logo} alt="Logo" className="auth-logo" />
                </motion.div>
                <motion.h1 
                  className="card-heading"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  Welcome Back
                </motion.h1>
                <motion.div 
                  className="tlborder"
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  transition={{ delay: 0.4 }}
                />
                <form onSubmit={handleSubmit}>
                  <motion.div 
                    className="form-group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </motion.div>
                  <motion.div 
                    className="form-group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </motion.div>
                  {error && (
                    <motion.div 
                      className="error-message"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      {error}
                    </motion.div>
                  )}
                  <motion.button 
                    type="submit" 
                    className="btn btn-primary"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 0 }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Signing in...' : 'Sign In'}
                  </motion.button>
                </form>
                <motion.div 
                  className="mt-3 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <Link to="/forgot-password" className="forgot-password">
                    Forgot Password?
                  </Link>
                </motion.div>
                <motion.div 
                  className="mt-3 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                >
                  <span className="signup-text">Don't have an account? </span>
                  <button onClick={toggleAuth} className="signup-link">Sign Up</button>
                </motion.div>
              </div>
            </motion.div>
          </>
        ) : (
          <>
            <motion.div
              key="signup-image"
              className="auth-image signup-bg"
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 300, opacity: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 30 }}
            />
            <motion.div
              key="signup-form"
              className="auth-form-side"
              data-islogin={false}
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 30 }}
            >
              <div className="auth-card">
                <motion.div
                  className="logo-container"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                </motion.div>
                <motion.h1
                  className="card-heading"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  {showOTP ? 'Verify Your Email' : 'Create Account'}
                </motion.h1>
                <motion.div
                  className="tlborder"
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  transition={{ delay: 0.4 }}
                />
                <div className="auth-form-container">
                  {!showOTP ? (
                    <form onSubmit={handleSubmit}>
                      <motion.div
                        className="form-group"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                      >
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Full Name"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          required
                        />
                      </motion.div>
                      <motion.div
                        className="form-group"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                      >
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Username"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          required
                        />
                      </motion.div>
                      <motion.div
                        className="form-group"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                      >
                        <input
                          type="email"
                          className="form-control"
                          placeholder="Email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </motion.div>
                      <motion.div
                        className="form-group password-group"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                      >
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                              <line x1="1" y1="1" x2="23" y2="23"></line>
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          )}
                        </button>
                      </motion.div>
                      <motion.div
                        className="form-group password-group"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9 }}
                      >
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className="form-control"
                          placeholder="Confirm Password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                              <line x1="1" y1="1" x2="23" y2="23"></line>
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          )}
                        </button>
                      </motion.div>
                      <motion.div
                        className="form-group"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1 }}
                      >
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={acceptPolicy}
                            onChange={() => setAcceptPolicy(!acceptPolicy)}
                          />
                          <span>
                            {' '}I agree to the{' '}
                            <span
                              className="link-like-text"
                              onClick={() => navigate('/privacy')}
                              style={{ color: '#007bff', cursor: 'pointer' }}
                            >
                              Privacy Policy & Terms
                            </span>
                          </span>
                        </label>
                        {policyError && <div className="error-message">{policyError}</div>}
                      </motion.div>
                      <motion.button
                        type="submit"
                        className="btn btn-primary"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1 }}
                        whileHover={{ y: -2 }}
                        whileTap={{ y: 0 }}
                      >
                        Sign Up
                      </motion.button>
                    </form>
                  ) : (
                    <motion.form
                      onSubmit={handleOTPSubmit}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <div className="otp-container">
                        <p className="otp-text">We've sent a verification code to your email</p>
                        <div className="otp-inputs">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              type="text"
                              name={`otp-${index}`}
                              value={digit}
                              onChange={(e) => handleOTPChange(index, e.target.value)}
                              onKeyDown={(e) => handleOTPKeyDown(index, e)}
                              maxLength={1}
                              className="otp-input"
                            />
                          ))}
                        </div>
                        <motion.button
                          type="submit"
                          className="btn btn-primary"
                          whileHover={{ y: -2 }}
                          whileTap={{ y: 0 }}
                        >
                          Verify
                        </motion.button>
                        <button
                          type="button"
                          className="resend-link"
                        >
                          Didn't receive the code? Resend
                        </button>
                      </div>
                    </motion.form>
                  )}
                  {!showOTP && (
                    <motion.div
                      className="mt-3 text-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.1 }}
                    >
                      <span className="auth-text">Already have an account? </span>
                      <button onClick={toggleAuth} className="auth-link">
                        Sign In
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Auth; 